import { Schema } from "effect";
import { DesktopWorkspaceGrantIdSchema } from "./desktop-workspace";
import { NonNegativeSafeIntegerSchema, PapersFlowBoundaryParseOptions } from "./standard";
import { utf8ByteLength } from "./utf8";

export const DESKTOP_TERMINAL_MAX_COLUMNS = 1_000;
export const DESKTOP_TERMINAL_MAX_ROWS = 500;
export const DESKTOP_TERMINAL_MAX_INPUT_BYTES = 64 * 1_024;
export const DESKTOP_TERMINAL_MAX_OUTPUT_EVENT_BYTES = 32 * 1_024;
export const DESKTOP_TERMINAL_MAX_ERROR_MESSAGE_BYTES = 240;
export const DESKTOP_TERMINAL_MAX_REPLAY_BYTES = 1_024 * 1_024;
export const DESKTOP_TERMINAL_MAX_SESSIONS_PER_GRANT = 8;
export const DESKTOP_TERMINAL_MAX_SESSIONS_GLOBAL = 16;

const hasControlCharacter = (value: string): boolean => {
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f)) return true;
  }
  return false;
};

export const DesktopTerminalSessionIdSchema = Schema.String.check(
  Schema.makeFilter(
    (value) =>
      /^pty_[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u.test(value) ||
      "must be a native terminal session identifier",
  ),
);

export const DesktopTerminalColumnsSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 1, maximum: DESKTOP_TERMINAL_MAX_COLUMNS }),
);
export const DesktopTerminalRowsSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 1, maximum: DESKTOP_TERMINAL_MAX_ROWS }),
);

export const DesktopTerminalSessionRefSchema = Schema.Struct({
  grantId: DesktopWorkspaceGrantIdSchema,
  sessionId: DesktopTerminalSessionIdSchema,
});

export const DesktopTerminalListInputSchema = Schema.Struct({
  grantId: DesktopWorkspaceGrantIdSchema,
});

export const DesktopTerminalSpawnInputSchema = Schema.Struct({
  grantId: DesktopWorkspaceGrantIdSchema,
  columns: DesktopTerminalColumnsSchema,
  rows: DesktopTerminalRowsSchema,
});

export const DesktopTerminalSpawnResultSchema = Schema.Struct({
  version: Schema.Literal(1),
  ...DesktopTerminalSessionRefSchema.fields,
});

export const DesktopTerminalListedSessionSchema = Schema.Struct({
  sessionId: DesktopTerminalSessionIdSchema,
});

export const DesktopTerminalListResultSchema = Schema.Struct({
  version: Schema.Literal(1),
  grantId: DesktopWorkspaceGrantIdSchema,
  sessions: Schema.Array(DesktopTerminalListedSessionSchema).check(
    Schema.isMaxLength(DESKTOP_TERMINAL_MAX_SESSIONS_PER_GRANT),
    Schema.makeFilter(
      (sessions) =>
        new Set(sessions.map((session) => session.sessionId)).size === sessions.length ||
        "terminal session identifiers must be unique",
    ),
  ),
});

export const DesktopTerminalAttachResultSchema = Schema.Struct({
  version: Schema.Literal(1),
  ...DesktopTerminalSessionRefSchema.fields,
  replay: Schema.String.check(
    Schema.makeFilter(
      (value) =>
        utf8ByteLength(value) <= DESKTOP_TERMINAL_MAX_REPLAY_BYTES ||
        `must contain at most ${DESKTOP_TERMINAL_MAX_REPLAY_BYTES} UTF-8 bytes`,
    ),
  ),
  replayTruncated: Schema.Boolean,
  nextSequence: NonNegativeSafeIntegerSchema,
});

export const DesktopTerminalWriteInputSchema = Schema.Struct({
  ...DesktopTerminalSessionRefSchema.fields,
  data: Schema.String.check(
    Schema.isMinLength(1),
    Schema.makeFilter(
      (value) =>
        utf8ByteLength(value) <= DESKTOP_TERMINAL_MAX_INPUT_BYTES ||
        `must contain at most ${DESKTOP_TERMINAL_MAX_INPUT_BYTES} UTF-8 bytes`,
    ),
  ),
});

export const DesktopTerminalResizeInputSchema = Schema.Struct({
  ...DesktopTerminalSessionRefSchema.fields,
  columns: DesktopTerminalColumnsSchema,
  rows: DesktopTerminalRowsSchema,
});

export const DesktopTerminalAckResultSchema = Schema.Null;

const DesktopTerminalEventBase = {
  version: Schema.Literal(1),
  ...DesktopTerminalSessionRefSchema.fields,
  sequence: NonNegativeSafeIntegerSchema,
} as const;

export const DesktopTerminalEventSchema = Schema.Union([
  Schema.Struct({
    ...DesktopTerminalEventBase,
    type: Schema.Literal("output"),
    data: Schema.String.check(
      Schema.isMinLength(1),
      Schema.makeFilter(
        (value) =>
          utf8ByteLength(value) <= DESKTOP_TERMINAL_MAX_OUTPUT_EVENT_BYTES ||
          `must contain at most ${DESKTOP_TERMINAL_MAX_OUTPUT_EVENT_BYTES} UTF-8 bytes`,
      ),
    ),
  }),
  Schema.Struct({
    ...DesktopTerminalEventBase,
    type: Schema.Literal("ended"),
  }),
]);

export const DesktopTerminalOperationSchema = Schema.Literals([
  "list",
  "attach",
  "spawn",
  "write",
  "resize",
  "kill",
]);

export const DesktopTerminalErrorCodeSchema = Schema.Literals([
  "invalid-input",
  "invalid-response",
  "io-failed",
  "pty-failed",
  "root-not-granted",
  "session-limit-reached",
  "session-not-found",
  "unavailable",
]);

export const DesktopTerminalErrorPayloadSchema = Schema.Struct({
  code: DesktopTerminalErrorCodeSchema,
  message: Schema.String.check(
    Schema.isMinLength(1),
    Schema.makeFilter(
      (value) =>
        (!hasControlCharacter(value) &&
          utf8ByteLength(value) <= DESKTOP_TERMINAL_MAX_ERROR_MESSAGE_BYTES) ||
        `must be a bounded terminal error message`,
    ),
  ),
  operation: Schema.optionalKey(DesktopTerminalOperationSchema),
  sessionId: Schema.optionalKey(DesktopTerminalSessionIdSchema),
  retryable: Schema.Boolean,
});

export type DesktopTerminalSessionId = typeof DesktopTerminalSessionIdSchema.Type;
export type DesktopTerminalSessionRef = typeof DesktopTerminalSessionRefSchema.Type;
export type DesktopTerminalListInput = typeof DesktopTerminalListInputSchema.Type;
export type DesktopTerminalSpawnInput = typeof DesktopTerminalSpawnInputSchema.Type;
export type DesktopTerminalSpawnResult = typeof DesktopTerminalSpawnResultSchema.Type;
export type DesktopTerminalListedSession = typeof DesktopTerminalListedSessionSchema.Type;
export type DesktopTerminalListResult = typeof DesktopTerminalListResultSchema.Type;
export type DesktopTerminalAttachResult = typeof DesktopTerminalAttachResultSchema.Type;
export type DesktopTerminalWriteInput = typeof DesktopTerminalWriteInputSchema.Type;
export type DesktopTerminalResizeInput = typeof DesktopTerminalResizeInputSchema.Type;
export type DesktopTerminalEvent = typeof DesktopTerminalEventSchema.Type;
export type DesktopTerminalOperation = typeof DesktopTerminalOperationSchema.Type;
export type DesktopTerminalErrorCode = typeof DesktopTerminalErrorCodeSchema.Type;
export type DesktopTerminalErrorPayload = typeof DesktopTerminalErrorPayloadSchema.Type;

export const encodeDesktopTerminalSessionRef = Schema.encodeSync(
  DesktopTerminalSessionRefSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopTerminalListInput = Schema.encodeSync(
  DesktopTerminalListInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopTerminalSpawnInput = Schema.encodeSync(
  DesktopTerminalSpawnInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopTerminalSpawnResult = Schema.decodeUnknownSync(
  DesktopTerminalSpawnResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopTerminalListResult = Schema.decodeUnknownSync(
  DesktopTerminalListResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopTerminalAttachResult = Schema.decodeUnknownSync(
  DesktopTerminalAttachResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopTerminalWriteInput = Schema.encodeSync(
  DesktopTerminalWriteInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopTerminalResizeInput = Schema.encodeSync(
  DesktopTerminalResizeInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopTerminalAckResult = Schema.decodeUnknownSync(
  DesktopTerminalAckResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopTerminalEvent = Schema.decodeUnknownSync(
  DesktopTerminalEventSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopTerminalErrorPayload = Schema.decodeUnknownSync(
  DesktopTerminalErrorPayloadSchema,
  PapersFlowBoundaryParseOptions,
);
